/**
 * Blog PropTypes
 *
 * PropType definitions for blog posts and article structures.
 *
 *
 * @module propTypes/blog
 */

import PropTypes from "prop-types";

/**
 * Blog post card shape
 * Simplified blog post data for card/grid displays
 *
 * @property {string} title - Post title
 * @property {string} slug - URL slug
 * @property {string} introduction - Brief introduction text
 * @property {Object} image - Featured image
 * @property {string} image.url - Image URL
 */
export const blogPostCardShape = PropTypes.shape({
  title: PropTypes.string,
  slug: PropTypes.string,
  introduction: PropTypes.string,
  image: PropTypes.shape({
    url: PropTypes.string,
  }),
});

/**
 * Blog post image shape
 * Represents an image within blog post content
 *
 * @property {string} title - Image title
 * @property {string} text - Image caption/description
 * @property {string} image - Image URL
 * @property {string} image_position - Position: "left" or "right"
 */
const blogPostImageShape = PropTypes.shape({
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  image_position: PropTypes.string.isRequired,
});

/**
 * Blog post header shape
 * Header section of a blog post
 *
 * @property {Object} image - Header image with metadata
 * @property {string} title - Post title
 * @property {string} date - Publication date
 */
export const blogPostHeaderShape = PropTypes.shape({
  image: PropTypes.shape({
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
  }),
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
});

/**
 * Blog post body shape
 * Main content section of a blog post
 *
 * @property {string} text - Main text content
 * @property {Array} images - Array of content images
 * @property {Array} links - Array of related links
 */
export const blogPostBodyShape = PropTypes.shape({
  text: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(blogPostImageShape),
  links: PropTypes.arrayOf(PropTypes.string),
});

/**
 * Complete blog post structure
 *
 * @property {Object} header - Post header
 * @property {Object} body - Post body content
 */
export const blogPostShape = {
  header: blogPostHeaderShape,
  body: blogPostBodyShape,
};
